import { FormControl, FormHelperText } from "@mui/material";
import { useMemo, type FC } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { Log, type ICustomField } from "@ryde/shared";

import { GroupTitleTemplate } from "../group_title.template";
import { PriceLevelItemAdd } from "./price-level-item.add";
import { PriceLevelRow } from "./price-level-row";
import {
  priceLevelSerialized,
  type IPriceLevelItem,
} from "./price-level.models";
import { PriceRateAdditionPricingStyled } from "./price-rate-addition-pricing.styled";

export const PriceRateAdditionPricing: FC<ICustomField> = ({
  name,
  formAttributes,
}) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext();
  const watchedValue = useWatch({ control, name });
  const pricingType = formAttributes?.priceRateAdditionPricingType;

  const priceLevels = useMemo(() => {
    if (!Array.isArray(watchedValue)) {
      return [];
    }

    return watchedValue as IPriceLevelItem[];
  }, [watchedValue]);

  const fromValues = useMemo(() => {
    return priceLevels.reduce((acc, item) => {
      if (item.from) {
        acc.push(item.from);
      }

      return acc;
    }, [] as number[]);
  }, [priceLevels]);

  const removeHandler = useEvent((index: number) => {
    Log.debug("Remove priceLevel handler fired for index ", index);
    priceLevels?.splice(index, 1);
    setValue(name, priceLevels || [], {
      shouldDirty: true,
      shouldValidate: true,
    });
  });

  const { disabled } = formAttributes || {};

  if (!pricingType) {
    return <div>{t("errors.pricingTypeNotSpecified")}</div>;
  }

  return (
    <PriceRateAdditionPricingStyled>
      <Controller
        name={name}
        control={control}
        render={({ fieldState: { error } }) => {
          return (
            <FormControl
              error={!!error}
              data-e2e-template="price-rate-addition-pricing-template"
            >
              <GroupTitleTemplate name={name} formAttributes={formAttributes} />

              <div className="price-rate-addition-pricing-container">
                {priceLevels?.map((priceLevel, index) => {
                  return (
                    <PriceLevelRow
                      key={`${index}_${priceLevelSerialized(priceLevel)}`}
                      fieldName={name}
                      index={index}
                      pricingType={pricingType}
                      fromValues={fromValues}
                      removeHandler={removeHandler}
                      parentControl={control}
                      parentSetValue={setValue}
                      disabledEdit={disabled}
                      disabledRemove={disabled}
                    />
                  );
                })}

                {!disabled && (
                  <PriceLevelItemAdd
                    fieldName={name}
                    index={priceLevels.length}
                    pricingType={pricingType}
                    fromValues={fromValues}
                    setValueOuter={setValue}
                    formAttributes={formAttributes}
                  />
                )}
              </div>
              {error && <FormHelperText>{error.message}</FormHelperText>}
            </FormControl>
          );
        }}
      />
    </PriceRateAdditionPricingStyled>
  );
};
